<script setup lang="ts">
import equal from "deep-equal";
import type { SerializedEditorState } from "lexical";
import {
  AutoLinkPlugin,
  CheckListPlugin,
  LinkPlugin as LexicalLinkPlugin,
  ListPlugin,
  MarkdownShortcutPlugin,
} from "lexical-vue";
import { computed, ref } from "vue";

import type TextEditorControls from "~/components/text/TextEditorControls.vue";
import { UserRole } from "~/shared/enums";
import type { Doc, DocUpdate } from "~/shared/types";
import { useDataStore, usePageStore, useUserStore } from "~/stores";

import { DOC_TELEPORT_KEY, URL_MATCHERS } from "./const";
import AttachmentPlugin from "./plugins/AttachmentPlugin.vue";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin.vue";
import CodeSelectorPlugin from "./plugins/CodeSelectorPlugin.vue";
import ColorPlugin from "./plugins/ColorPlugin.vue";
import FloatingToolbar from "./plugins/FloatingToolbar.vue";
import LinkPlugin from "./plugins/LinkPlugin.vue";
import ManageTextFormatPlugin from "./plugins/ManageTextFormatPlugin.vue";
import PasteMarkdownPlugin from "./plugins/PasteMarkdownPlugin.vue";
import PlaceholderPlugin from "./plugins/PlaceholderPlugin.vue";
import QuoteSectionPlugin from "./plugins/QuoteSectionPlugin.vue";
import RecommendationPlugin from "./plugins/RecommendationPlugin.vue";
import RelationshipPlugin from "./plugins/RelationshipPlugin.vue";
import ShortcutsPlugin from "./plugins/ShortcutsPlugin.vue";
import SplitPlugin from "./plugins/SplitPlugin.vue";
import SubscribeEventPlugin from "./plugins/SubscribeEventPlugin.vue";
import TableOfContentsPlugin from "./plugins/TableOfContentsPlugin.vue";
import TablePlugin from "./plugins/TablePlugin.vue";
import TabPlugin from "./plugins/TabPlugin.vue";
import TextInsertPlugin from "./plugins/TextInsertPlugin.vue";
import TextSavePlugin from "./plugins/TextSavePlugin.vue";
import TrailingParagraphPlugin from "./plugins/TrailingParagraphPlugin.vue";
import { EntityTypeaheadPlugin, RecommendationTypeaheadPlugin } from "./plugins/typeahead";
import TextEditor from "./TextEditor.vue";
import { TRANSFORMERS } from "./transformers";
import { FULL_DART_EDITOR_NODES } from "./utils";

const props = defineProps<{
  doc: Doc;
  textEditorControls?: InstanceType<typeof TextEditorControls> | null;
  mini?: boolean;
  noPlaceholder?: boolean;
  editorClasses?: string;
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const textEditor = ref<InstanceType<typeof TextEditor> | null>(null);
const attachmentPlugin = ref<InstanceType<typeof AttachmentPlugin> | null>(null);
const recommendationPlugin = ref<InstanceType<typeof RecommendationPlugin> | null>(null);
const textInsertPlugin = ref<InstanceType<typeof TextInsertPlugin> | null>(null);

const initialState = computed(() => props.doc.text);

const hasFocus = computed(() => !!textEditor.value?.hasFocus);

const aiEnabled = computed(() => pageStore.isOnline && userStore.isRoleGte(UserRole.MEMBER));

const saveLocally = async (duid: string, content: SerializedEditorState) => {
  const oldEditorDuids = props.doc.editorDuids;
  const oldSubscriberDuids = props.doc.subscriberDuids;
  await dataStore.updateDocs([{ duid, text: content }], { noBackend: true });
  const sameEditors = equal(props.doc.editorDuids, oldEditorDuids, { strict: true });
  const sameSubscribers = equal(props.doc.subscriberDuids, oldSubscriberDuids, { strict: true });
  if (sameEditors && sameSubscribers) {
    return;
  }

  const update: DocUpdate = { duid };
  if (!sameEditors) {
    update.editorDuids = props.doc.editorDuids;
  }
  if (!sameSubscribers) {
    update.subscriberDuids = props.doc.subscriberDuids;
  }
  dataStore.updateDocs([update]);
};

const focus = (start?: boolean) => textEditor.value?.focus(start);

const focusEnd = () => textEditor.value?.focusEnd();

const focusNearestBlock = (clientX: number, clientY: number) => textEditor.value?.focusNearestBlock(clientX, clientY);

const addAttachments = (files: File[]) => attachmentPlugin.value?.addAttachments(files);

const handleDrop = (event: DragEvent) => attachmentPlugin.value?.handleDrop(event);

const openRecommendations = () => recommendationPlugin.value?.openRecommendations();

const insert = (text: string) => textInsertPlugin.value?.insert(text);

const setEditorState = (state: SerializedEditorState) => textEditor.value?.setEditorState(state);

defineExpose({
  addAttachments,
  focus,
  focusEnd,
  focusNearestBlock,
  handleDrop,
  openRecommendations,
  insert,
  setEditorState,
});
</script>

<template>
  <TextEditor
    ref="textEditor"
    :key="doc.duid"
    namespace="doc"
    :nodes="FULL_DART_EDITOR_NODES"
    :initial-state="initialState"
    :small-leading="mini"
    :editor-classes="editorClasses"
    base-text
    :disabled="mini"
    :placeholder="props.noPlaceholder ? '' : mini ? 'Empty doc' : `Write anything${aiEnabled ? ', / for AI' : ''}`"
    always-borderless
    :collaboration="mini ? undefined : { namespace: 'doc', id: doc.duid }">
    <template #default>
      <CodeHighlightPlugin />
      <ColorPlugin />
      <TablePlugin />
      <template v-if="!mini">
        <!-- lexical-vue plugins -->
        <AutoLinkPlugin :matchers="URL_MATCHERS" />
        <CheckListPlugin />
        <LexicalLinkPlugin />
        <ListPlugin />
        <MarkdownShortcutPlugin :transformers="TRANSFORMERS" />
        <!-- custom plugins -->
        <AttachmentPlugin ref="attachmentPlugin" />
        <CodeSelectorPlugin editable />
        <EntityTypeaheadPlugin :has-focus="hasFocus" mentions-only />
        <LinkPlugin @link-changes="() => {}" />
        <ManageTextFormatPlugin />
        <PasteMarkdownPlugin />
        <PlaceholderPlugin />
        <QuoteSectionPlugin />
        <RecommendationPlugin
          v-if="aiEnabled"
          ref="recommendationPlugin"
          :duid="doc.duid"
          :toolbar-teleport-key="DOC_TELEPORT_KEY"
          base-text />
        <RecommendationTypeaheadPlugin v-if="aiEnabled" :title="doc.title" />
        <RelationshipPlugin :doc="doc" />
        <ShortcutsPlugin :text-editor-controls="textEditorControls" />
        <SplitPlugin :doc="doc" />
        <SubscribeEventPlugin :doc="doc" />
        <TableOfContentsPlugin v-if="!pageStore.isMobile && !mini" />
        <TabPlugin />
        <TextInsertPlugin ref="textInsertPlugin" />
        <TextSavePlugin :duid="doc.duid" :content="doc.text" :has-focus="hasFocus" immediate @save="saveLocally" />
        <TrailingParagraphPlugin />
        <!-- toolbars -->
        <FloatingToolbar :teleport-key="DOC_TELEPORT_KEY" :title="doc.title" show-on-scroll />
      </template>
    </template>
  </TextEditor>
</template>
